import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import s from "./style.module.scss";
import _ from "lodash";
import Loading from "../Loading/index.jsx";
import RenderSurveyInClassicMode from "../RenderSurveyInClassic";
import RenderSurveyInGuidedMode from "../RenderSurveyInGuided";
import { setFocus } from "../../../utils/setFocus";
import {
  CONDITIONS,
  DEFAULT_PARENT_ID,
  FACES_VALUES,
  PIPING_FROM,
  MULTI_SELECT_PIPING_QUESTIONS,
  QUESTION_TYPES,
  RESUME,
  OTHER,
} from "../../constants";
import {
  removeEmptyBlocks,
  separateBlockQuestionsOnAPage,
  separateLoopedBlockQuestions,
} from "../../../services/blockUtils/blockUtils";
import {
  checkIsEmpty,
  fetchResult,
  getConditiontags,
  fetchEquationVariableOptionsFromAnswers,
  fetchPTLOptionsFromHiddenAnswers,
  fetchCustomVariableOptionsFromAnswers,
} from "../../../services/commons/index.js";
import { EQUATION } from "../../../services/constants/index.js";
import { PASS_THROUGH_LINK } from "../../../constants/constantsForSidePanel.js";

//Disable the priortized display
const PRIORTIZED_DISPLAY = false;

const ConditionalSurvey = ({
  hasConditions,
  hasAnswerOptionLogic,
  hasDependentEV,
  hiddenAnswers,
  previewing,
  currentLang,
  save,
  onAutoSave,
  resumeType,
}) => {
  const { t } = useTranslation("surveyTaker");
  const [answers, setAnswers] = useState({});
  const [terminateAnswers, setTerminateAnswers] = useState({});
  const [activeGroupQuestions, setActiveGroupQuestions] = useState([]);
  const [hiddenGroupQuestions, setHiddenGroupQuestions] = useState([]);
  const [activeSkipConditions, setActiveSkipConditions] = useState([]);
  // Commenting it out for later, if active terminate conditions are required.
  // const [activeTerminateConditions, setActiveTerminateConditions] = useState(
  //   []
  // );
  const [surveyTerminateConditions, setSurveyTerminateConditions] = useState([
    {},
  ]);
  const [terminateSurvey, setTerminateSurvey] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState(0);
  const [nextOrSubmit, setNextOrSubmit] = useState(false);
  const [hasNonHiddenQuestions, setHasNonHiddenQuestions] = useState();
  const [resolvedQuestions, setResolvedQuestions] = useState([]);
  const [hiddenVariables, setHiddenVariables] = useState({});
  const [pipingAnswers, setPipingAnswers] = useState({});
  const loopedQuestions = useRef(new Set());
  const [nextActiveQuestions, setNextActiveQuestions] = useState([]);
  const questionsMap = useRef({});
  const variableAnswerRef = useRef({});
  const [renderAutoSavedSurvey, setRenderAutoSavedSurvey] = useState(false);

  const {
    currentSurveyId,
    surveyExperienceType,
    data: survey,
  } = useSelector((state) => state.survey);
  const { saveInProgress } = useSelector((state) => state.submissionEdit);
  const { data: autoSavedData, dataExist: autoSaveExist } = useSelector(
    (state) => state.autoSave
  );
  //when it mounts, sort the questions in the operable format
  useEffect(() => {
    // clear shouldDisplay flag on mount
    survey[currentSurveyId].questions = _.map(
      survey[currentSurveyId].questions,
      (question) => _.omit(question, ["shouldDisplay"])
    );

    //create a map question with id as key to get question O(1) complexity
    survey[currentSurveyId].questions.forEach(
      (q) => (questionsMap.current[q.id] = q)
    );

    survey[currentSurveyId].questions = survey[currentSurveyId].questions.map(
      (question) => {
        const questionDependencies = [];
        const choicesDependencies = [];
        const terminateDependencies = [];
        let choices;
        if (question.type === "ranking") {
          choices = question.rankingOptions;
        } else {
          choices = question.choices;
        }

        question[CONDITIONS]?.displayConditions?.forEach((condition) => {
          questionDependencies.push(condition.question);
        });

        question[CONDITIONS]?.terminateConditions?.forEach((condition) => {
          terminateDependencies.push(condition.question);
        });

        choices?.forEach((choice) => {
          choice.logic?.conditions?.forEach((condition) => {
            choicesDependencies.push(condition.question);
          });
        });

        return {
          ...question,
          questionDependencies,
          choicesDependencies,
          terminateDependencies,
        };
      }
    );
    setSurveyTerminateConditions(
      getAllTerminateConditions(survey[currentSurveyId].questions)
    );

    if(resumeType !== RESUME)
      sortByFirstRelevantCondition(survey[currentSurveyId].questions);
    window.scrollTo(0, 0);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasNonHiddenQuestions === false) {
      return handleValidateQuestion(answers); //TODO : need to check the usage and validity
    }
    if (Array.isArray(activeGroupQuestions)) {
      return setFocus(activeGroupQuestions[0]?.id + "container");
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupQuestions, hasNonHiddenQuestions]);

  useEffect(() => {
    if (nextOrSubmit) {
      progressSurvey();
      setNextOrSubmit(false);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOrSubmit]);

  useEffect(() => {
    if (resumeType === RESUME && autoSaveExist) {
      questionsMap.current = autoSavedData.questions_map;
      loopedQuestions.current = new Set(autoSavedData.looped_questions);
      setAnswers(autoSavedData.answers);
      setActiveSkipConditions(autoSavedData.active_skip_conditions);
      setResolvedQuestions(autoSavedData.resolved_questions);
      setPipingAnswers(autoSavedData.piping_answers);
      setNextActiveQuestions(autoSavedData.next_active_qestions);
      setRenderAutoSavedSurvey(true);
    }
  }, [resumeType]);

  useEffect(() => {
    if (renderAutoSavedSurvey) {
      sortByFirstRelevantCondition(autoSavedData.questions);
    }
  }, [renderAutoSavedSurvey]);

  const onAutoSaveConditiional = (data) => {
    const dataToSave = {
      ...data,
      questions: [...activeGroupQuestions, ...hiddenGroupQuestions],
      active_skip_conditions: activeSkipConditions,
      resolved_questions: resolvedQuestions,
      piping_answers: pipingAnswers,
      looped_questions: [...loopedQuestions.current],
      questions_map: questionsMap.current,
      next_active_qestions: nextActiveQuestions,
    };
    if (!dataToSave.questions?.length) return;
    onAutoSave(dataToSave);
  };

  function saveConditionalSurvey(status) {
    const pipingAnswersNew = {};
    for (const item of Object.entries(pipingAnswers || {})) {
      const id = item[0];
      const tags = item[1];
      const pipingParentID = tags.pipingParentID;
      delete tags["pipingParentID"];
      pipingAnswersNew[pipingParentID] ||= [];
      const [answer, answerContainOther] = replaceOtherWithAnswer(id);
      if (answerContainOther) {
        delete answers[questionsMap.current[id].otherQuestionId];
      }
      pipingAnswersNew[pipingParentID].push({
        piping: tags,
        answer,
      });
      //remove looped Question
      if (pipingParentID !== id) delete answers[id];
    }
    save({ ...answers, ...terminateAnswers }, pipingAnswersNew, status);
  }

  const findVariableAnswer = (item) => {
    return getConditiontags(
      {
        questionId: item?.source?.parentId,
        questionType: item?.source?.parentType,
      },
      answers[item?.source?.parentId],
      survey[currentSurveyId].questions,
      answers
    );
  };
  // A function to get all the terminate conditions of survey
  const getAllTerminateConditions = (questions) => {
    const surveyAllTerminateConditions = questions
      .filter((question) => question.type === "terminate")
      .map((question) => ({
        [question.id]: question[CONDITIONS]?.terminateConditions,
      }));

    return surveyAllTerminateConditions;
  };

  //a function which can be used to take the questions, and group them into "active group" and "hidden group" based on the first question with a relevant condition in the questions argument

  const sortByFirstRelevantCondition = (questions) => {
    const sliceQuestionId = determineSkipConditionsValidity();
    let activeGroup = [],
      hiddenGroup = [],
      activeSkipConditions = [],
      // activeTerminateConditions = [[]],
      prevQuestionWasHidden = false;
    const prioritizedDisplay = [];
    let skipPoint = 0;
    let addAllToHidden = false;
    if (sliceQuestionId) {
      for (let x = 0; x < questions.length; x++) {
        if (questions[x].id.toString() === sliceQuestionId.toString()) {
          //cut off the previous questions
          //Note: Here the display logic of this question is ignored.
          skipPoint = x;
          // if (questions[x]["#conditions"].skipConditions.length > 0) {
          //   skipPoint = x;
          // } else {
          //   //TODO: Check with Mayoor on the impl.
          //   /*
          //               Now considering this question's display condition also, even when skip conditions satisfied.
          //               So no implicit priority and confusion.
          //               */
          //   skipPoint = x;

          //   /*
          //               activeGroup.push(questions[x]);
          //               skipPoint = x + 1;
          //               */
          // }
          break;
        }
      }
      questions = questions.slice(skipPoint);
    } else {
      // filter out questions that have been decided not to be displayed
      questions = questions.filter((q) => q.shouldDisplay !== false);
    }
    //Determine the relevant questions starting next to the question where CQ flow skipped to

    for (let i = 0; i < questions.length; i++) {
      // check current question has choice with conditions
      let choices;
      if (questions[i].type === "ranking") {
        choices = questions[i].rankingOptions;
      } else {
        choices = questions[i].choices;
      }

      if (choices?.length === 0) {
        choices = null;
      }

      if (questions[i].type === "variable" && activeGroup.length === 0) {
        activeGroup.push(questions[i]);
        addAllToHidden = true;
        continue;
      }

      let currentQuestionChoiceWithCondition = choices?.find((choice) => {
        return choice.logic?.conditions?.length > 0 || choice.source;
      });

      if (
        (questions[i].shouldDisplay === undefined &&
          activeGroup.length > 0 &&
          (currentQuestionChoiceWithCondition ||
            questions[i].type === EQUATION)) ||
        addAllToHidden
      ) {
        hiddenGroup.push(questions[i]);
        addAllToHidden = true;
        continue;
      }

      // Check for terminate conditions
      const quesCondition = questions[i][CONDITIONS];
      if (quesCondition && quesCondition?.terminateConditions?.length > 0) {
        // If the previous question is hidden,
        // and terminate question reached, add all the questions to hidden group (act as Page break)
        if (prevQuestionWasHidden === true) {
          hiddenGroup.push(questions[i]);
          addAllToHidden = true;
        } else if (
          questions[i].type === "terminate" &&
          activeGroup.length !== 0
        ) {
          //Find how many consecutive terminate questions types are present, after current terminate question.
          const activeGroupIndex = questions
            .slice(i + 1)
            .findIndex(
              (question) =>
                question.type !== "terminate" && question.type !== undefined
            );

          // Add all the terminate question's to the active group, till the non terminate question come.
          activeGroup =
            activeGroupIndex !== -1
              ? questions.slice(0, i + activeGroupIndex + 1)
              : questions;

          hiddenGroup = questions.slice(activeGroup.length);
          break;
        } else {
          // If no question's in active group, that means the terminate question
          // conditions can not be validated against any previous questions,
          // hence continue, to next question.
          continue;
        }

        // Commenting below code
        // If we decide to take all the responses till the activeGroupTerminateConditions reaches,
        // pass the determineTerminateConditionsValidity(activeGroupTerminateConditions);
        // For now if any of the terminate condition validates in the entire survey, then the survey terminates there.
        // Reason we are not doing it now, if any questions skip conditions satisfy & terminate questions gets skipped
        // So Terminate Questions be skipped, even if the skip conditions satisfy, Terminate has higher priority.

        // const activeGroupTerminateConditions = activeGroup
        //   .filter((question) => question.type === "terminate")
        //   .map((question) => question[CONDITIONS]?.terminateConditions);

        // activeTerminateConditions = activeGroupTerminateConditions;
      }

      //first check if skip conditions, if so stop iterating here
      if (
        hiddenGroup.length === 0 &&
        quesCondition &&
        quesCondition.skipConditions.length > 0
      ) {
        //treat it slightly differently if the first question has a skip condition
        if (i !== 0) {
          //If the question contains both display & skip conditions
          if (quesCondition.displayConditions.length > 0) {
            activeGroup = questions
              .slice(0, i)
              .filter((q) => q.shouldDisplay !== false);
            hiddenGroup = questions
              .slice(i)
              .filter((q) => q.shouldDisplay !== false);
          } else {
            //Include the current question to active group and shouldDisplay is true or undefined
            activeGroup = questions
              .slice(0, i + 1)
              .filter((q) => q.shouldDisplay !== false);
            hiddenGroup = questions
              .slice(i + 1)
              .filter((q) => q.shouldDisplay !== false);
          }
        } else {
          activeGroup.push(questions[0]);
          hiddenGroup = questions
            .slice(i + 1)
            .filter((q) => q.shouldDisplay !== false);
        }

        activeSkipConditions = quesCondition.skipConditions;
        break;
        //if it has display conditions
      }
      // Check for display conditions.
      else if (
        (quesCondition && quesCondition.displayConditions.length > 0) ||
        questions[i].isPipedFrom
      ) {
        //has been decided and its conditions are met
        if(prevQuestionWasHidden === true && activeGroup.length > 0) {
          hiddenGroup.push(questions[i]);
        }
        else if (questions[i].shouldDisplay === true || 
           (quesCondition.displayConditions.length === 0 &&
          questions[i].isPipedFrom &&
          questions[i].shouldDisplay !== false &&
          checkPipingConditionStatisfied(questions[i]) &&
          !activeGroupIncludePipedQuestion(activeGroup, questions[i])
        )) {
          activeGroup.push(questions[i]);
          prioritizedDisplay.push(questions[i]);
        } else if (questions[i].shouldDisplay === undefined) {
          hiddenGroup.push(questions[i]);
          prevQuestionWasHidden = true;
        }

        // if question has answer options logic
      } else if (
        choices &&
        choices.length > 0 &&
        (currentQuestionChoiceWithCondition?.logic?.conditions?.length > 0 ||
          currentQuestionChoiceWithCondition?.source)
      ) {
        if (
          questions[i].shouldDisplay === undefined &&
          activeGroup.length === 0
        ) {
          activeGroup.push(questions[i]);
        } else {
          hiddenGroup.push(questions[i]);
          prevQuestionWasHidden = true;
        }
      }

      //if it doesn't have any conditions & is its turn to display
      if (
        !quesCondition ||
        (quesCondition.displayConditions.length === 0 &&
          (!choices ||
            (choices &&
              choices.length > 0 &&
              !currentQuestionChoiceWithCondition)) &&
          !questions[i].isPipedFrom)
      ) {
        if (prevQuestionWasHidden === true && activeGroup.length !== 0) {
          hiddenGroup.push(questions[i]);
        } else {
          activeGroup.push(questions[i]);
        }
      }
    }
    //if it should skip
    if (sliceQuestionId) {
      // This prioritized display is to override the skip logic by displaying the questions
      // which has display logic and theirs conditions are satisfied,
      // even though the preceeding question's skip logic got satisfied and to bypass these questions.
      // (Erik's comment: if we have prioritized display, include them even though they would have been skipped.)

      // To enable, set PRIORTIZED_DISPLAY to true; by default it is set to false.

      if (PRIORTIZED_DISPLAY === true && prioritizedDisplay.length > 0) {
        activeGroup = prioritizedDisplay.concat(activeGroup);
      }
    }
    hiddenGroup = removeOrphanQuestions(hiddenGroup, activeGroup);
    // remove blocks from active group
    activeGroup = activeGroup.filter((q) => q.type !== EQUATION);
    let hiddenVariablesCopy = { ...hiddenVariables };
    setHiddenVariables((prevHiddenVar) => {
      return getNewHiddenVariablesAndActiveGroup(prevHiddenVar, activeGroup)
        .hiddenVariablesCopy;
    });

    let newHiddenVarAndActiveGroup = getNewHiddenVariablesAndActiveGroup(
      hiddenVariablesCopy,
      activeGroup
    );

    activeGroup = newHiddenVarAndActiveGroup.activeGroup;
    hiddenVariablesCopy = newHiddenVarAndActiveGroup.hiddenVariablesCopy;

    let hasNonHiddenQuestion;
    let hasOtherQuestionInPTL;
    const otherPTLQuestionIds = [];
    for (const question of activeGroup) {
      if (question.type === "hidden") {
        const hiddenAnswer = hiddenAnswers[question.jsonKey];
        if (!hiddenAnswer && question.required === true) {
          window.console.error(t("surveyTaker:hiddenQuestionErrorLog"));
        } else {
          setAnswers((prevAnswers) => {
            return {
              ...prevAnswers,
              [question.id]: hiddenAnswer ? hiddenAnswer.toString() : "",
            };
          });
        }
        if (!hasNonHiddenQuestion) {
          hasNonHiddenQuestion = false;
        }
      } else if (
        question.formattingType &&
        question.formattingType === "passThroughLink"
      ) {
        if (question.other && question.otherQuestionId.length !== 0) {
          hasOtherQuestionInPTL = true;
          otherPTLQuestionIds.push(question.otherQuestionId);
        }
        const hiddenAnswer = hiddenAnswers[question.questionLabel]; // Updated to questionLabel instead id
        if (!hiddenAnswer && question.required === true) {
          window.console.error(t("surveyTaker:hiddenQuestionErrorLog"));
        } else {
          setAnswers((prevAnswers) => {
            return {
              ...prevAnswers,
              [question.id]: hiddenAnswer ? hiddenAnswer.toString() : "",
            };
          });
        }
        if (!hasNonHiddenQuestion) {
          hasNonHiddenQuestion = false;
        }
      } else if (question.type === EQUATION) {
        if (!hasNonHiddenQuestion) {
          hasNonHiddenQuestion = false;
        }
      } else {
        hasNonHiddenQuestion = true;
      }
    }
    if (hasOtherQuestionInPTL) {
      otherPTLQuestionIds.forEach((eachPTLQuestionId) => {
        const ptlOtherAnswer = hiddenAnswers[eachPTLQuestionId];
        setAnswers((prevAnswers) => {
          return {
            ...prevAnswers,
            [eachPTLQuestionId]: ptlOtherAnswer
              ? ptlOtherAnswer.toString()
              : "",
          };
        });
      });
    }

    // adding Hidden variable answers to submission
    for (let question of questions) {
      if (question.type === EQUATION || question.type === "variable") {
        let hiddenVarAnswer = [];

        if (question.type === "variable") {
          question.choices.forEach((item) => {
            if (
              item.logic.check === "alwaysTrue" ||
              item.logic.check === "trueWithCondition" ||
              item.logic.check === "falseWithCondition"
            ) {
              if (
                item?.source?.parentId &&
                item.value?.includes("Response from") &&
                item?.source?.parentId &&
                !checkIsEmpty(answers[item?.source?.parentId])
              ) {
                const variableAnswer = findVariableAnswer(item);
                hiddenVarAnswer.push(variableAnswer);
              } else {
                hiddenVarAnswer.push(item.label);
              }
            }
          });

          if (hiddenVarAnswer.length > 0) {
            setAnswers((prevAnswers) => {
              return {
                ...prevAnswers,
                [question.id]: hiddenVarAnswer,
              };
            });
          }
        } else {
          setAnswers((prevAnswers) => {
            return {
              ...prevAnswers,
              [question.id]: `${fetchResult(question.equations, prevAnswers)}`,
            };
          });
        }
      }
    }

    // const activeGroupQuestionIds = returnActiveGroupQuestionIds(activeGroup);

    activeGroup = analyzePipingQuestions(activeGroup);

    // this is to divide each block iteration on a separate page
    const blocksOnASeparatePage = separateBlockQuestionsOnAPage(
      activeGroup,
      hiddenGroup
    );
    const { currentPageQuestions, nextPageQuestions } =
      separateLoopedBlockQuestions(blocksOnASeparatePage.activeGroupQuestions);
    activeGroup = currentPageQuestions;
    setNextActiveQuestions(nextPageQuestions);
    hiddenGroup = blocksOnASeparatePage.hiddenGroupQuestions;

    if (removeEmptyBlocks(activeGroup).length === 0) {
      if (removeEmptyBlocks(hiddenGroup).length === 0) {
        return saveConditionalSurvey();
      } else {
        // if active grp is empty, and we have questions in hidden group, we wll compute choices in analyze fn and render new active gp
        hiddenGroup = analyzeFutureDisplayConditions(
          hiddenGroup,
          hiddenVariablesCopy
        );
        return sortByFirstRelevantCondition(hiddenGroup);
      }
    }

    if (hasNonHiddenQuestion === false && previewing !== "true") {
      // used strict equality here for setHasNonHiddenQuestion
      setActiveGroupQuestions(activeGroup);
      setHiddenGroupQuestions(hiddenGroup);
      // Comment out for future, if decided to take responses till the terminate postion in survey,
      //  inspite it conditions validated earlier only.
      // setActiveTerminateConditions(activeTerminateConditions);
      setActiveSkipConditions(activeSkipConditions);
      setHasNonHiddenQuestions(false);
    } else if (hasNonHiddenQuestions === false && hasNonHiddenQuestion) {
      setHasNonHiddenQuestions(hasNonHiddenQuestion);
    }
    setActiveGroupQuestions(activeGroup);
    setHiddenGroupQuestions(hiddenGroup);
    // Comment out for future, if decided to take responses till the terminate postion in survey,
    //  inspite it conditions validated earlier only.
    // setActiveTerminateConditions(activeTerminateConditions);
    setActiveSkipConditions(activeSkipConditions);
  };

  const activeGroupIncludePipedQuestion = (activeGroup, question) => {
    const activeGroupQuestionIds = []
    for (const question of activeGroup) {
      activeGroupQuestionIds.push(question.id.toString());
    }

    for (const item of question[CONDITIONS].pipingConditions[PIPING_FROM]) {
      if (activeGroupQuestionIds.includes(item.id)) {
        return true;
      }
    }
    return false;
  }

  // If there are custom variables on top then this resolves hidden varibale choices conditions and filter the active group
  function getNewHiddenVariablesAndActiveGroup(
    hiddenVariablesCopy,
    activeGroup
  ) {
    let hiddenVariableComputedAnswers = {};
    if (activeGroup.length > 0) {
      let allCustomVariablesInActiveGroup = true;
      for (let i = 0; i < activeGroup.length; i++) {
        if (
          activeGroup[i]?.type !== "variable" &&
          activeGroup[i]?.formattingType !== "passThroughLink" &&
          activeGroup[i]?.type !== EQUATION
        ) {
          allCustomVariablesInActiveGroup = false;
        }
        if (activeGroup[i].choices) {
          for (let j = 0; j < activeGroup[i].choices.length; j++) {
            const item = activeGroup[i].choices[j];
            if (
              item?.logic?.check === "alwaysFalse" &&
              activeGroup[i]?.type !== "variable"
            ) {
              activeGroup[i].choices[j].shouldRenderChoice = false;
            }
            if (activeGroup[i]?.type === "variable") {
              if (item?.logic?.check === "alwaysFalse") {
                hiddenVariableComputedAnswers[item.label] = false;
                item.shouldRenderHV = false;
              } else if (item?.logic?.check === "alwaysTrue") {
                hiddenVariableComputedAnswers[item.label] = true;
                item.shouldRenderHV = true;
              }
            }
          }
        }
        if (activeGroup[i]?.type === "variable") {
          hiddenVariablesCopy = {
            ...hiddenVariablesCopy,
            [activeGroup[i].id]: hiddenVariableComputedAnswers,
          };
          hiddenVariableComputedAnswers = {};
        }
      }
      if (allCustomVariablesInActiveGroup) {
        activeGroup = activeGroup.filter((item) => item.type != "variable");
      }
    }

    return { hiddenVariablesCopy, activeGroup };
  }

  /*
    Removes any orphan questions.
    i.e Orphan questions - The pending questions which are not dependant on any other pending questions.
    but dependant on the non participating questions which CQ flow came across.
    eg:
    Suppose there are 3 question Q1,Q2,Q3
    i added skip logic from Q1 -->Q3 and added a piping/disply from Q2--> Q4. now Q4 will removed
    */

  const removeOrphanQuestions = (hiddenGroup, activeGroup) => {
    const questionIdsInCurrentActiveGroup = [];
    const answeredQuestionIds = Object.keys(getVariableAnswers());
    for (let i = 0; i < activeGroup.length; i++) {
      questionIdsInCurrentActiveGroup.push(activeGroup[i].id.toString());
    }
    const processedHiddenQuestionIds = [];
    for (let i = 0; i < hiddenGroup.length; i++) {
      const hiddenQuesCondition = hiddenGroup[i]["#conditions"];
      if (
        hiddenGroup[i].shouldDisplay === undefined &&
        hiddenQuesCondition &&
        hiddenQuesCondition.displayConditions.length > 0
      ) {
        const displayConditions = hiddenQuesCondition.displayConditions;
        for (let j = 0; j < displayConditions.length; j++) {
          if (
            processedHiddenQuestionIds.includes(
              displayConditions[j].question.toString()
            ) ||
            questionIdsInCurrentActiveGroup.includes(
              displayConditions[j].question.toString()
            )
          ) {
            hiddenGroup[i].toRemove = false;
            break;
          } else {
            !hiddenGroup[i].dependenciesPending &&
              (hiddenGroup[i].toRemove = true);
          }
        }
      } else if (hiddenGroup[i].isPipedFrom) {
        for (const item of hiddenGroup[i][CONDITIONS].pipingConditions[
          PIPING_FROM
        ]) {
          if (
            processedHiddenQuestionIds.includes(item.id) ||
            questionIdsInCurrentActiveGroup.includes(item.id) ||
            answeredQuestionIds.includes(item.id)
          ) {
            hiddenGroup[i].toRemove = false;
          } else {
            //remove piping question if atleast one piping condition fail
            hiddenGroup[i].toRemove = true;
            break;
          }
        }
      } else {
        hiddenGroup[i].toRemove = false;
      }
      processedHiddenQuestionIds.push(hiddenGroup[i].id.toString());
    }
    return hiddenGroup.filter((q) => q.toRemove === false);
  };

  const checkStringExist = (conditionArray, answer, type) => {
    if (answer) {
      for (let i = 0; i < conditionArray.length; i++) {
        const existFlag = answer
          .toLowerCase()
          .includes(conditionArray[i].toLowerCase().trim());
        if (existFlag) {
          return type === "contains" ? true : false;
        }
      }
    }
    return type === "contains" ? false : true;
  };

  const checkHVConditions = (answer, question, hiddenVariablesVal) => {
    const consideredHiddenVariables =
      Object.keys(hiddenVariablesVal).length > 0
        ? hiddenVariablesVal
        : hiddenVariables;
    return {
      shouldRenderHV: consideredHiddenVariables?.[question]?.[answer],
    };
  };

  //determine if a condition is valid based on the current answer
  const determineConditionValidity = (condition, newAnswers = {}) => {
    let answer = answers[condition.question] || newAnswers[condition.question];
    let conditionAnswer = condition.answer;
    if (condition.parentId && conditionAnswer.includes("Response from")) {
      conditionAnswer = getConditiontags(
        {
          questionId: condition.parentId,
          questionType: condition.parentType,
        },
        answers[condition.parentId] || newAnswers[condition.parentId],
        survey[currentSurveyId].questions,
        answers
      );
    }
    if (!answer) {
      if (
        condition.type === "isEmpty" ||
        condition.type === "doesNotContain" ||
        condition.type === "isNotSelected"
      ) {
        return true;
      } else {
        return false;
      }
    } else if (condition.type === "isNotEmpty") {
      return true;
    }

    if (!condition.answer) {
      return false;
    }

    switch (condition.type) {
      case "contains":
        return checkStringExist(conditionAnswer, answer, "contains");
      case "doesNotContain":
        return checkStringExist(conditionAnswer, answer, "doesNotContain");
      case "isSelected":
        //incase of checkbox response check
        if (answer instanceof Array) {
          return answer.includes(conditionAnswer);
        } else {
          return answer == conditionAnswer;
        }
      case "isNotSelected":
        //incase of checkbox response check
        if (answer instanceof Array) {
          return !answer.includes(conditionAnswer);
        } else {
          return answer != conditionAnswer;
        }
      case "isGreaterThan":
        return Number(answer) > Number(conditionAnswer);
      case "isGreaterThanOrEqual":
        return Number(answer) >= Number(conditionAnswer);
      case "isLessThan":
        return Number(answer) < Number(conditionAnswer);
      case "isLessThanOrEqual":
        return Number(answer) <= Number(conditionAnswer);
      case "equals":
        return answer.toLowerCase() === String(conditionAnswer).toLowerCase();
      case "notEquals":
        return answer.toLowerCase() !== String(conditionAnswer).toLowerCase();
      case "isPromoter":
        return Number(answer) > 8;
      case "isDetractor":
        return Number(answer) < 7;
      case "isPassive":
        return Number(answer) === 7 || Number(answer) === 8;
      case "isNotPromoter":
        return Number(answer) <= 8;
      case "isNotDetractor":
        return Number(answer) >= 7;
      case "isNotPassive":
        return Number(answer) !== 7 && Number(answer) !== 8;
      default:
        return false;
    }
  };

  //return an array of question ids in the current active group
  const returnActiveGroupQuestionIds = () => {
    const activeGroupQuestionIds = [];
    for (const question of activeGroupQuestions) {
      activeGroupQuestionIds.push(question.id.toString());
    }
    // for (let i = 0; i < activeGroup.length; i++) {
    //   activeGroupQuestionIds.push(activeGroup[i].id.toString());
    // }
    return activeGroupQuestionIds;
  };

  function getVariableAnswers() {
    let updatedAnswer = _.cloneDeep(answers);
    const allVariableAnswers = fetchCustomVariableOptionsFromAnswers(
      survey,
      currentSurveyId,
      updatedAnswer
    );
    variableAnswerRef.current = {
      ...variableAnswerRef.current,
      ...allVariableAnswers,
    };
    const allEquationAnswers = fetchEquationVariableOptionsFromAnswers(
      survey,
      currentSurveyId,
      updatedAnswer
    );
    variableAnswerRef.current = {
      ...variableAnswerRef.current,
      ...allEquationAnswers,
    };
    const allPTLAnswers = fetchPTLOptionsFromHiddenAnswers(
      survey[currentSurveyId].questions,
      hiddenAnswers,
      updatedAnswer
    );
    variableAnswerRef.current = {
      ...variableAnswerRef.current,
      ...allPTLAnswers,
    };
    return {
      ...answers,
      ...allVariableAnswers,
      ...allEquationAnswers,
      ...allPTLAnswers,
    };
  }

  function checkPipingConditionStatisfied(question) {
    const answeredQuestionsIds = Object.keys(getVariableAnswers());
    const parentBlock = survey?.[currentSurveyId]?.questions?.find(
      (q) => q.id === question.parentId
    );

    if (question.isPipedFrom || parentBlock?.isPipedFrom) {
      for (const item of question[CONDITIONS].pipingConditions[PIPING_FROM]) {
        if (!answeredQuestionsIds.includes(item.id)) {
          return false;
        }
      }
      return true;
    } else {
      return false;
    }
  }

  //check if any of the future condition dependent questions care about the answers to questions in the active group
  const returnRelevantFutureDisplayConditionsQuestionIds = () => {
    const futureQuestionConditionalIds =
        returnHiddenGroupDisplayConditionalQuestionIds(),
      //activeGroupQuestionIds = returnActiveGroupQuestionIds(),
      relevantFutureQuestionIds = [];
    const activeGroupQuestionIds = [
      ...resolvedQuestions,
      ...returnActiveGroupQuestionIds(),
    ];

    for (let i = 0; i < futureQuestionConditionalIds.length; i++) {
      if (
        activeGroupQuestionIds.includes(
          futureQuestionConditionalIds[i].toString()
        )
      ) {
        relevantFutureQuestionIds.push(futureQuestionConditionalIds[i]);
      }
    }

    //piping given less priority, even one question have piping and display condition, it show if display condition true regardless of piping status
    //check all piping condition of a question statisfied, then add to relevantFutureQuestionIds if it not already added.
    for (let i = 0; i < hiddenGroupQuestions.length; i++) {
      const qid = hiddenGroupQuestions[i].id.toString();
      if (
        checkPipingConditionStatisfied(hiddenGroupQuestions[i]) &&
        !relevantFutureQuestionIds.includes(qid)
      ) {
        relevantFutureQuestionIds.push(qid);
      }
    }

    if (relevantFutureQuestionIds.length > 0) {
      return relevantFutureQuestionIds;
    }
    return null;
  };

  //return an array of ids, upon which the display of future questions depends
  const returnHiddenGroupDisplayConditionalQuestionIds = () => {
    const hiddenGroupDisplayConditionalQuestionIds = [],
      hiddenQuestions = hiddenGroupQuestions;
    for (let i = 0; i < hiddenQuestions.length; i++) {
      let choices;
      if (hiddenQuestions[i].type === "ranking") {
        choices = hiddenQuestions[i].rankingOptions;
      } else {
        choices = hiddenQuestions[i].choices;
      }
      if (
        hiddenQuestions[i][CONDITIONS] &&
        hiddenQuestions[i][CONDITIONS].displayConditions
      ) {
        for (
          let x = 0;
          x < hiddenQuestions[i][CONDITIONS].displayConditions.length;
          x++
        ) {
          hiddenGroupDisplayConditionalQuestionIds.push(
            hiddenQuestions[i][CONDITIONS].displayConditions[x].question
          );
        }
      }
      if (choices && choices.length > 0) {
        choices.forEach((choice) => {
          if (choice.logic?.conditions?.length > 0) {
            choice.logic.conditions.forEach((condition) => {
              hiddenGroupDisplayConditionalQuestionIds.push(condition.question);
            });
          } else if (choice?.source && choice.logic?.conditions?.length === 0) {
            hiddenGroupDisplayConditionalQuestionIds.push(
              choice?.source?.questionId
            );
          }
        });
      }
    }
    return hiddenGroupDisplayConditionalQuestionIds;
  };

  //analyze the relevant future display conditions and prepare those objects which should be modified
  /* 
        questionsSkippedByLogic - contains all the questions object which are between the firts question of the CQ page 
        and till the question the CQ flow skips to.
    */
  const analyzeFutureDisplayConditions = (
    futureQuestionsArr,
    hiddenVariablesDefault
  ) => {
    let futureQuestions = _.cloneDeep(futureQuestionsArr);
    let hiddenVariableComputedAnswers = {};
    let hiddenVariablesCopy = hiddenVariablesDefault || { ...hiddenVariables };
    const answerIds = Object.keys(answers);
    const resolvedQuestionsCopy = [
      ...resolvedQuestions,
      ...returnActiveGroupQuestionIds(),
    ];

    for (let x = 0; x < futureQuestions.length; x++) {
      // checking if parent block shouldDisplay was false then no
      // need to process further as this questions will also not be visible
      if (
        futureQuestions[x].parentId !== DEFAULT_PARENT_ID &&
        futureQuestions[x].parentShouldDisplay === false
      ) {
        futureQuestions[x].shouldDisplay = false;
        continue;
      }
      hiddenVariableComputedAnswers = {};
      if (futureQuestions[x][CONDITIONS]?.displayConditions.length > 0) {
        let displayConditions =
          futureQuestions[x][CONDITIONS].displayConditions;
        let shouldDisplay;
        for (let z = 0; z < displayConditions.length; z++) {
          let shortCircuitEvaluation = false;

          if (displayConditions[z].modifier === "or") {
            //Short circuit evaluation - break the rest of display conditions' evaluation when OR is encountered and the past conditions are evaluated to true.
            if (shouldDisplay === true) {
              shortCircuitEvaluation = true;
            }
          }
          if (shortCircuitEvaluation === false) {
            const displayConditionEvaluation =
              determineConditionValidity(displayConditions[z]) === true
                ? true
                : false;

            //For first condition just assign the current condition's evaluation result
            if (z === 0) {
              shouldDisplay = displayConditionEvaluation;
              //For condition with AND operator perform and operation with the previous result and the current condition's evaluation result
            } else if (displayConditions[z].modifier === "and") {
              shouldDisplay = shouldDisplay && displayConditionEvaluation;

              // For condition with OR operator just assign the current condition's evaluation result.
              // Here the previous result will always be false as the short circuit logic failed to break the for loop
            } else if (displayConditions[z].modifier === "or") {
              shouldDisplay = displayConditionEvaluation;
            }
          }

          if (shortCircuitEvaluation === true) {
            break;
          }

          if (
            answerIds.includes(displayConditions[z].question) ||
            answerIds.includes(displayConditions[z].question.toString())
          ) {
            futureQuestions[x].shouldDisplay = shouldDisplay;
          } else if (displayConditions[z].modifier === "and") {
            // If any of the display conditions is false set should to undefined, so that is is added to hidden group.
            delete futureQuestions[x].shouldDisplay;
            break;
          }
        }
      }

      if (futureQuestions[x].choicesDependencies.length > 0) {
        if (futureQuestions[x].shouldDisplay) {
          let dependenciesPending = false;
          futureQuestions[x].choicesDependencies.forEach((item) => {
            if (
              resolvedQuestionsCopy.indexOf(item) < 0 &&
              Object.keys(hiddenVariablesCopy).indexOf(item) < 0
            ) {
              dependenciesPending = true;
            }
          });
          if (dependenciesPending && futureQuestions.length !== 1) {
            futureQuestions[x].dependenciesPending = dependenciesPending;
            delete futureQuestions[x].shouldDisplay;
          }
          // im assuming questiondependencies will not cause issues
        }
      }

      // check if choice dependencies resolved
      //run choice filter
      let choices;
      if (futureQuestions[x].type === "ranking") {
        choices = futureQuestions[x].rankingOptions;
      } else {
        choices = futureQuestions[x].choices;
      }

      if (choices && choices.length > 0) {
        choices.forEach((choice, ind) => {
          if (
            choice?.source?.parentId &&
            choice.value?.includes("Response from") &&
            choice?.source?.parentType !== EQUATION &&
            choice?.source?.parentType !== "variable" &&
            !checkIsEmpty(answers[choice?.source?.parentId])
          ) {
            const variableAnswer = findVariableAnswer(choice);
            choices[ind].label = variableAnswer;
            choices[ind].value = `Value_${variableAnswer}`;
          }
          if (choice.logic?.check === "alwaysTrue") {
            if (futureQuestions[x].type === "variable") {
              choices[ind].shouldRenderHV = true;
              hiddenVariableComputedAnswers[choices[ind].label] = true;
            } else {
              choices[ind].shouldRenderChoice = true;
            }
          } else if (choice.logic?.check === "alwaysFalse") {
            if (futureQuestions[x].type === "variable") {
              choices[ind].shouldRenderHV = false;
              hiddenVariableComputedAnswers[choices[ind].label] = false;
            } else {
              choices[ind].shouldRenderChoice = false;
            }
          } else {
            if (choice.logic?.conditions?.length > 0) {
              const choiceConditions = choice.logic?.conditions;
              let shouldRenderChoice;
              let newAnswer;
              for (let z = 0; z < choiceConditions.length; z++) {
                let shortCircuitEvaluation = false;
                if (choiceConditions[z].modifier === "or") {
                  if (shouldRenderChoice === true) {
                    shortCircuitEvaluation = true;
                  }
                }
                if (shortCircuitEvaluation === false) {
                  let choiceConditionEvaluation;
                  let hiddenConditionEvaluation;
                  if (
                    choiceConditions[z].type === "isTrue" ||
                    choiceConditions[z].type === "isFalse"
                  ) {
                    newAnswer =
                      choiceConditions[z].answer.substring(
                        choiceConditions[z].answer.indexOf(".") + 1
                      ) || choiceConditions[z].answer;
                    newAnswer = newAnswer.trim();
                    hiddenConditionEvaluation = checkHVConditions(
                      newAnswer,
                      choiceConditions[z].question,
                      hiddenVariablesCopy
                    );

                    if (hiddenConditionEvaluation !== undefined) {
                      if (choiceConditions[z].type === "isTrue") {
                        choiceConditionEvaluation =
                          hiddenConditionEvaluation?.shouldRenderHV;
                      } else {
                        choiceConditionEvaluation =
                          !hiddenConditionEvaluation?.shouldRenderHV;
                      }
                    } else {
                      choiceConditionEvaluation =
                        futureQuestions[x].choices[ind].shouldRenderChoice;
                    }
                  } else {
                    if (
                      choiceConditions[z].type === "isSelected" ||
                      choiceConditions[z].type === "isNotSelected"
                    ) {
                      newAnswer = choiceConditions[z].answer.substring(
                        choiceConditions[z].answer.indexOf(".") + 1
                      );
                      newAnswer = newAnswer.trim();
                      if (
                        choiceConditions[z].parentId &&
                        newAnswer.includes("Response from")
                      ) {
                        newAnswer = getConditiontags(
                          {
                            questionId: choiceConditions[z].parentId,
                            questionType: choiceConditions[z].parentType,
                          },
                          answers[choiceConditions[z].parentId],
                          survey[currentSurveyId].questions,
                          answers
                        );
                      }
                      choiceConditions[z].answer = newAnswer;
                    }

                    choiceConditionEvaluation =
                      determineConditionValidity(choiceConditions[z]) === true
                        ? true
                        : false;
                  }

                  //ForHidden Variable Check
                  //For first condition just assign the current condition's evaluation result
                  if (z === 0) {
                    shouldRenderChoice = choiceConditionEvaluation;
                    //For condition with AND operator perform and operation with the previous result and the current condition's evaluation result
                  } else if (choiceConditions[z].modifier === "and") {
                    shouldRenderChoice =
                      shouldRenderChoice && choiceConditionEvaluation;
                    /*
                              For condition with OR operator just assign the current condition's evaluation result.
                              Here the previous result will always be false as the short circuit logic failed to break the for loop
                              */
                  } else if (choiceConditions[z].modifier === "or") {
                    shouldRenderChoice = choiceConditionEvaluation;
                  }
                  if (futureQuestions[x].type === "variable") {
                    choices[ind].shouldRenderHV =
                      choice.logic?.check === "trueWithCondition"
                        ? shouldRenderChoice
                        : !shouldRenderChoice;
                  } else {
                    choices[ind].shouldRenderChoice =
                      choice.logic?.check === "trueWithCondition"
                        ? shouldRenderChoice
                        : !shouldRenderChoice;
                  }
                }

                if (shortCircuitEvaluation === true) {
                  break;
                }
              }
            }
          }

          if (choices[ind].shouldRenderHV !== undefined) {
            hiddenVariableComputedAnswers[choices[ind].label] =
              choices[ind].shouldRenderHV;
          }
        });
      }
      if (futureQuestions[x].type === "variable") {
        hiddenVariablesCopy = {
          ...hiddenVariablesCopy,
          [futureQuestions[x].id]: hiddenVariableComputedAnswers,
        };
      }

      // adding block shouldDispay to its questions
      if (futureQuestions[x].type === QUESTION_TYPES.BLOCK) {
        futureQuestions.forEach((q) => {
          if (q.parentId === futureQuestions[x].id) {
            q.parentShouldDisplay = futureQuestions[x].shouldDisplay;
          }
        });
      }
    }

    setHiddenVariables((prevHiddenVar) => {
      return { ...prevHiddenVar, ...hiddenVariablesCopy };
    });
    //setHiddenVariables(hiddenVariablesCopy);
    // this.setState({
    //   hiddenVariables,
    // });
    let isConsecutive = true;
    futureQuestions = futureQuestions.map((item) => {
      if (item.type === "variable" && isConsecutive) {
        item.shouldDisplay = false;
      } else if (item.shouldDisplay !== false) {
        isConsecutive = false;
      }
      return item;
    });
    return futureQuestions;
  };

  //check if skip condition is met
  const determineSkipConditionsValidity = () => {
    const conditions = activeSkipConditions;

    for (let i = 0; i < conditions.length; i++) {
      if (determineConditionValidity(conditions[i]) === true) {
        return conditions[i].skipDestination;
      }
    }
    return false;
  };

  const determineTerminateConditionsValidity = (
    answers,
    surveyAllTerminateConditions
  ) => {
    //TODO: Check and remove this duplicate assignment
    surveyAllTerminateConditions =
      surveyAllTerminateConditions ||
      getAllTerminateConditions(survey[currentSurveyId].questions);
    let terminateConditionsValidity = false;
    let terminateUpdates = {};
    for (let terminateConditions of surveyAllTerminateConditions) {
      let conditions =
        !!Object.values(terminateConditions).length &&
        Object.values(terminateConditions)[0];
      let terminateConditionId = Object.keys(terminateConditions)[0];

      const conditionsModifier = conditions[0]?.modifier || "and";
      // Every Terminate condition with "and" modifier need to be satisfied
      if (conditionsModifier === "and" && conditions.length > 0) {
        terminateConditionsValidity = conditions.every((condition) => {
          return (
            !!answers[condition.question] &&
            determineConditionValidity(condition, answers)
          );
        });
      }

      // Atleast one Terminate condition with "or" modifier need to be satisfied
      else if (conditionsModifier === "or" && conditions.length > 0) {
        terminateConditionsValidity = conditions.some((condition) => {
          return (
            !!answers[condition.question] &&
            determineConditionValidity(condition, answers)
          );
        });
      }

      // Accumulate updates in the terminateUpdates object
      if (terminateConditionId) {
        terminateUpdates[terminateConditionId] =
          terminateConditionsValidity || false;
      }
      if (terminateConditionsValidity) {
        break;
      }
    }
    // Update state outside the loop
    setTerminateAnswers((prev) => ({
      ...prev,
      ...terminateUpdates,
    }));
    //TODO: Check and remove this duplicate assignment
    setTerminateSurvey(terminateConditionsValidity);
    return terminateConditionsValidity;

    // const conditions = activeTerminateConditions;
    // const conditionsModifier = conditions[0]?.modifier || "and";
    // if (conditionsModifier === "and" && conditions.length > 0) {
    //   return conditions.every((condition) =>
    //     determineConditionValidity(condition)
    //   );
    // } else if (conditionsModifier === "or" && conditions.length > 0) {
    //   return conditions.some((condition) =>
    //     determineConditionValidity(condition)
    //   );
    // } else {
    // return false;
    // }
  };

  useEffect(() => {
    terminateSurvey && saveConditionalSurvey("terminate");
  }, [terminateSurvey]);

  const progressSurvey = () => {
    let skipDestination;
    // scroll to top
    window.scrollTo(0, 0);
    setCompletedQuestions(
      (prevCompletedQues) => prevCompletedQues + activeGroupQuestions.length
    );

    let futureDisplayQuestionIds =
      returnRelevantFutureDisplayConditionsQuestionIds(); //including piping id
    const skipConditionsValidity = determineSkipConditionsValidity();

    // let questionsSkippedByLogic = [];
    // if (skipConditionsValidity !== false && skipDestination !== "skipToEnd") {
    // const idOfSkipToTargetQuestion = skipConditionsValidity;
    // const idOfFirstQuestionInActiveGroup =
    // activeGroupQuestions.length !== 0 ? activeGroupQuestions[0].id : -1;
    // if (idOfFirstQuestionInActiveGroup !== -1) {
    // const indexOfFirstQuestionInActiveGroup = findIndexByQuestionId(
    //   survey[currentSurveyId].questions,
    //   idOfFirstQuestionInActiveGroup
    // );
    // const indexOfSkipToTargetQuestion = findIndexByQuestionId(
    //   survey[currentSurveyId].questions,
    //   idOfSkipToTargetQuestion
    // );
    /*This array may have questions which is displayed in the current CQ page but taking the first question would be the ideal;
                because some questions would not be displayed which are in between the first question and the last quesiton of the current CQ page.*/
    // questionsSkippedByLogic = survey[currentSurveyId].questions.slice(
    //   indexOfFirstQuestionInActiveGroup + 1,
    //   indexOfSkipToTargetQuestion
    // );
    // }
    // }

    let futureQuestions = _.cloneDeep(hiddenGroupQuestions);
    if (futureDisplayQuestionIds !== null) {
      futureQuestions = analyzeFutureDisplayConditions(futureQuestions);
    }

    if (skipConditionsValidity !== false) {
      skipDestination = skipConditionsValidity;

      if (skipDestination === "skipToEnd") {
        //submit survey
        return saveConditionalSurvey();
      } else {
        return sortByFirstRelevantCondition(futureQuestions);
      }
    }
    // if there are some looped questions already
    // generated then first show them
    if (nextActiveQuestions.length) {
      const { currentPageQuestions, nextPageQuestions } =
        separateLoopedBlockQuestions(nextActiveQuestions);
      setActiveGroupQuestions(currentPageQuestions);
      setNextActiveQuestions(nextPageQuestions);
      return;
    }
    sortByFirstRelevantCondition(futureQuestions);
  };

  const findIndexByQuestionId = (questions, questionId) => {
    let index = -1;
    for (let i = 0; i < questions.length; i++) {
      if (questions[i].id === questionId) {
        index = i;
        break;
      }
    }
    return index;
  };

  const handleValidateQuestion = useCallback(
    (newAnswers) => {
      setAnswers((prevAnswers) => {
        return {
          ...prevAnswers,
          ...newAnswers,
        };
      });

      // Whenever next or submit button pressed, check for survey terminate condition validity
      const terminateConditionSuccess = determineTerminateConditionsValidity(
        { ...answers, ...newAnswers },
        getAllTerminateConditions(survey[currentSurveyId].questions)
      );

      // Update the survey answer's & terminate question answer's
      setAnswers((prevAnswers) => {
        return {
          ...prevAnswers,
          ...terminateAnswers,
        };
      });

      setResolvedQuestions((prevResolvedQuestions) => [
        ...prevResolvedQuestions,
        ...returnActiveGroupQuestionIds(),
      ]); 
      // Update the Terminate survey state
      setTerminateSurvey(terminateConditionSuccess);
      // Only if not terminated go to next page or submit.
      !terminateConditionSuccess && setNextOrSubmit(true);
    },
    [setAnswers, setNextOrSubmit, answers, surveyTerminateConditions, activeGroupQuestions]
  );

  function analyzePipingQuestions(activeGroupArr) {
    const futureQuestions = _.cloneDeep(activeGroupArr);
    let newFutureQuestions = [];
    futureQuestions.forEach((question) => {
      const parentBlock = survey[currentSurveyId]?.questions?.find(
        (q) => q.id === question.parentId
      );
      //display the piping question if it doesn't excluded by display condition.
      //should display will be false if it is setten else it will undefined/true,, false!=undefined
      if (
        (question.isPipedFrom || parentBlock?.isPipedFrom) &&
        question.shouldDisplay !== false &&
        checkPipingConditionStatisfied(question)
      ) {
        question.shouldDisplay = true;
        newFutureQuestions = [
          ...newFutureQuestions,
          ...analyzeLooping(question, parentBlock),
        ];
      } else if (!question.isPipedFrom) {
        newFutureQuestions.push(question);
      }
    });
    return newFutureQuestions;
  }

  //return new hidden question list with looped questions and piping tag replaced.
  function analyzeLooping(question, parentBlock) {
    // if this questions was itself generated
    // via piping or it is a block then don't process it further
    if (question.generatedViaPiping || question.type === QUESTION_TYPES.BLOCK) {
      return [question];
    }
    let pipingConditions =
      question[CONDITIONS].pipingConditions[PIPING_FROM] || [];

    const parentBlockPipings = (
      (parentBlock && parentBlock[CONDITIONS]?.pipingConditions?.pipingFrom) ||
      []
    ).filter(
      (piping) => !pipingConditions.find((p) => p.pipingId === piping.pipingId)
    );

    pipingConditions = [...pipingConditions, ...parentBlockPipings];

    let newQuestions = [question];

    while (pipingConditions.length) {
      const pipingDetails = pipingConditions.shift();
      const pipingFromQuestion = questionsMap.current[pipingDetails.id];
      let selectedAnswers = answers[pipingFromQuestion.id];
      if (pipingFromQuestion.type === QUESTION_TYPES.VARIABLE) {
        selectedAnswers = Object.values(
          variableAnswerRef.current[pipingFromQuestion.id] || {}
        );
      }
      if (
        pipingFromQuestion.type === QUESTION_TYPES.EQUATION ||
        pipingFromQuestion.formattingType === PASS_THROUGH_LINK
      ) {
        selectedAnswers =
          variableAnswerRef.current[pipingFromQuestion.id] || "";
      }
      const isLooped = loopedQuestions.current.has(pipingDetails.id);
      let tempQuestions = [];
      newQuestions.forEach((question) => {
        if (
          MULTI_SELECT_PIPING_QUESTIONS.has(pipingFromQuestion.type) ||
          pipingFromQuestion.type === QUESTION_TYPES.VARIABLE ||
          isLooped
        ) {
          selectedAnswers = findLoopedQuestionAnswers(pipingDetails.id);
          loopedQuestions.current.add(question.id);
          //generate all possible qstns
          const newQuestions = loopQuestion(
            question,
            selectedAnswers,
            pipingDetails
          );
          tempQuestions = [...tempQuestions, ...newQuestions];
        } else {
          const replacedQuestion = replacePipingTagWithAnswer(
            question,
            selectedAnswers,
            pipingDetails
          );
          tempQuestions.push(replacedQuestion);
        }
      });
      newQuestions = tempQuestions;
    }
    return newQuestions;
  }

  function replaceOtherWithAnswer(id) {
    let answerContainOther = false;
    let selectedAnswers = [answers[id]];
    if (questionsMap.current[id].type === QUESTION_TYPES.VARIABLE) {
      selectedAnswers = Object.values(variableAnswerRef.current[id] || {});
    }
    if (
      questionsMap.current[id].type === QUESTION_TYPES.EQUATION ||
      questionsMap.current[id].formattingType === PASS_THROUGH_LINK
    ) {
      selectedAnswers = variableAnswerRef.current[id] || "";
    }
    const newAnswers = selectedAnswers.flat().map((answer) => {
      if (answer === OTHER && questionsMap.current[id].otherQuestionId) {
        answerContainOther = true;
        return answers[questionsMap.current[id]?.otherQuestionId];
      } else {
        return answer;
      }
    });
    return [newAnswers, answerContainOther];
  }

  // if piped from a looping question, selectedAnswers will union of selected options in all looped questions.
  function findLoopedQuestionAnswers(id) {
    const loopedAnswers = replaceOtherWithAnswer(id)[0];
    for (const [qid, piping] of Object.entries(pipingAnswers)) {
      if (piping.pipingParentID === id) {
        const answer = replaceOtherWithAnswer(qid)[0];
        loopedAnswers.push(answer);
      }
    }
    return [...new Set(loopedAnswers.flat())];
  }

  function loopQuestion(question, selectedAnswers, pipingDetails) {
    const questions = [];
    selectedAnswers.forEach((selectedAns, index) => {
      let newQuestion = cloneQuestion(question, index);
      newQuestion = replacePipingTagWithAnswer(
        newQuestion,
        selectedAns,
        pipingDetails
      );
      // add a flag indicating that this question is a generated one
      questions.push({ ...newQuestion, generatedViaPiping: true });
    });
    return questions;
  }

  function cloneQuestion(question, index) {
    let newQuestion = _.cloneDeep(question);
    newQuestion.id = `${
      new Date().getTime() + Math.ceil(Math.random() * 1000)
    }`;
    //looping first question id should same as actual question.
    if (index === 0) {
      newQuestion.id = question.id;
    }
    if (newQuestion.other) {
      newQuestion.otherQuestionId =
        newQuestion.id + Math.ceil(Math.random() * 10);
    }

    (questionsMap.current[newQuestion.id] = newQuestion),
      (newQuestion.pipingParentID = question.pipingParentID || question.id);
    //copy all piping tag of crnt qstn
    setPipingAnswers((prev) => {
      return {
        ...prev,
        [newQuestion.id]: _.cloneDeep(prev[question.id] || {}),
      };
    });
    return newQuestion;
  }

  function replacePipingTagWithAnswer(question, selectedAns, pipingDetails) {
    const questionObj = _.cloneDeep(question);
    const pipingFromQuestion = questionsMap.current[pipingDetails.id];
    const pipingAnswer = {};

    let answer = generateLabelAnswer(selectedAns, pipingFromQuestion);
    pipingAnswer[pipingDetails.pipingTag] = answer;
    //here we need translation of choices if currentlang!=selected lang.
    if (pipingFromQuestion.choices) {
      answer =
        pipingFromQuestion.choices.find(
          (choice) => choice.value === `Value_${answer}`
        )?.label || answer;
    }

    const answerTag = `<span class=${s.pipingTag}>${answer}</span>`;

    questionObj.question = questionObj.question.replace(
      pipingDetails.pipingTag,
      answerTag
    );
    const pipingParentID = question.pipingParentID || question.id;
    setPipingAnswers((prev) => {
      return {
        ...prev,
        [questionObj.id]: prev[questionObj.id] || {},
        [questionObj.id]: {
          ...prev[questionObj.id],
          ...pipingAnswer,
          pipingParentID,
        },
      };
    });

    return questionObj;
  }

  function generateLabelAnswer(answer, pipingFromQuestion) {
    switch (pipingFromQuestion.type) {
      case QUESTION_TYPES.RADIOS:
        if (
          pipingFromQuestion.otherQuestionId &&
          answers[pipingFromQuestion.otherQuestionId]
        ) {
          answer = answers[pipingFromQuestion.otherQuestionId];
        }
        break;
      case QUESTION_TYPES.DATE_AND_TIME:
        answer = `${answer.date ? answer.date : ""}${
          answer.date && answer.time ? ", " : ""
        }${answer.time ? answer.time : ""}`;
        break;
      case QUESTION_TYPES.FACES:
        answer = t(FACES_VALUES[answer]);
        break;
      case QUESTION_TYPES.RANGE:
        answer = pipingFromQuestion.optionsList
          ? pipingFromQuestion.optionsList[answer - 1]
          : answer;
        break;
      case QUESTION_TYPES.CHECK_BOXES:
        if (
          pipingFromQuestion.otherQuestionId &&
          answers[pipingFromQuestion.otherQuestionId] &&
          answer === OTHER
        ) {
          answer = answers[pipingFromQuestion.otherQuestionId];
        }
        break;
    }
    return answer;
  }
  const executeTestingFunctions = () => {
    const questions = [
      {
        id: "16385104314600",
      },
      {
        id: "16385104314601",
      },
      {
        id: "16385104314602",

        "#conditions": {
          displayConditions: [],
          skipConditions: [],
        },
      },
    ];

    findIndexByQuestionId(questions);
    sortByFirstRelevantCondition(questions);
  };

  if (saveInProgress === true) {
    return <Loading container={s.container} />;
  }

  const handleAnswerCallback = (newAnswer) => {
    const newAnswers = {
      ...answers,
      ...newAnswer,
    };
    if (hasDependentEV) {
      const evQuestions = hiddenGroupQuestions.filter(
        (que) => que.type === EQUATION
      );
      evQuestions.forEach((que) => {
        newAnswers[que.id] = `${fetchResult(que.equations, newAnswers)}`;
      });
    }
    setAnswers(newAnswers);
  };
  return (
    <>
      <div
        className={s.dummyButton}
        data-testid="execute-testing-functions"
        onClick={executeTestingFunctions}
      />
      {surveyExperienceType === "guided" ? (
        <RenderSurveyInGuidedMode
          hasConditions={hasConditions}
          hasDependentEV={hasDependentEV}
          hasAnswerOptionLogic={hasAnswerOptionLogic}
          activeGroup={activeGroupQuestions}
          hiddenGroup={hiddenGroupQuestions}
          surveyId={currentSurveyId}
          currentLang={currentLang}
          handleValidateQuestion={handleValidateQuestion}
          onAutoSave={onAutoSaveConditiional}
          resumeType={resumeType}
          questionsCount={Object.keys(questionsMap.current).length}
          determineTerminateConditionsValidity={
            determineTerminateConditionsValidity
          }
          nextActiveQuestions={nextActiveQuestions}
        />
      ) : (
        <RenderSurveyInClassicMode
          hasConditions={hasConditions}
          activeGroup={activeGroupQuestions}
          hiddenGroup={hiddenGroupQuestions}
          surveyId={currentSurveyId}
          currentLang={currentLang}
          handleAnswerCallback={handleAnswerCallback}
          handleValidateQuestion={handleValidateQuestion}
          onAutoSave={onAutoSaveConditiional}
          resumeType={resumeType}
          completedQuestions={completedQuestions}
          nextActiveQuestions={nextActiveQuestions}
        />
      )}
    </>
  );
};

export default ConditionalSurvey;
